import { getRangeFromValue } from "@omnia/shared/layers/viewers/range"
import { color, format, getMorganIndexTextFromValue, isUndefinedOrNull, SoilUnit, NumericValueColorProvider as ColorProvider, IDatalayerViewerFactory, IDatalayerMapInstance, NumericValuesViewer, ZoneTextLocation, soilColors } from "../../omnia"
import { IDataLayerRange, IDataLayerType, LayerTypeId } from "../../types"


export class TerraMapViewer extends NumericValuesViewer {

    public static getFactory(typeId: LayerTypeId, units: () => SoilUnit): IDatalayerViewerFactory {

        const element = LayerTypeId[typeId]

        return {

            typeId,
            name: format.formatChemicalCompoundInTextAsUnicode(element),
            key: element,

            createViewer: (layer) => new TerraMapViewer(layer, element, units),

        }

    }


    protected morganAnalysis: boolean
    protected morganRanges: IDataLayerRange[]

    constructor(layer: IDatalayerMapInstance, element: string, private unitsProvider: () => SoilUnit) {

        super(layer)

        this._dataProperty = element

    }

    protected setupNumericLayer(type: IDataLayerType, colorProvider: ColorProvider, ranges?: IDataLayerRange[]) {

        const morgan = this.morganAnalysis = usesMorganAnalysis(this._layer)

        if (morgan) {

            const style = type.AlternateStyles.find(s => s.Key == "morgan")

            this.morganRanges = style?.Ranges

        }

        super.setupNumericLayer(type, colorProvider, ranges)

        if (morgan) {

            this.keyId = {
                ...this.keyId,
                id: `${this.keyId.id}-morgan`,
                name: `${this.keyId.name} (Morgan)`
            }

        }

    }

    protected getDefaultRanges(type: IDataLayerType) {

        return this.morganRanges || super.getDefaultRanges(type)

    }

    getValueText(value: number, location: ZoneTextLocation) {

        if (this.showIndexUnits()) {

            const range = getRangeFromValue(this._ranges, value)

            if (range) {
                const { label } = this.convertRangeToIndexWithLabel(range)
                return label
            }

        }

        return super.getValueText(value, location)

    }

    protected getKeyFromRanges() {

        if (this.showIndexUnits()) {

            return this._ranges.map(r => {

                const { min, label } = this.convertRangeToIndexWithLabel(r)

                return {
                    value: min,
                    text: label,
                    color: color.colorToCss(r.color)
                }

            })

        }

        return super.getKeyFromRanges()

    }

    protected convertRangeToIndexWithLabel(range: { min: number, max: number }) {

        const [min, max] = this.convertRangeToIndex(range)

        const label = isUndefinedOrNull(max)
            ? `>=  ${min}`
            : (min == max)
                ? `${min}`
                : `${min} - ${max}`

        return { min, max, label }
    }

    protected showIndexUnits() {
        const units = this.unitsProvider ? this.unitsProvider() : null
        return (units == SoilUnit.Index)
    }

    private convertRangeToIndex({ min, max }: { min: number, max: number }) {

        const { _dataProperty: prop } = this

        return this.morganAnalysis
            ? [
                getMorganIndexText(min, prop),
                getMorganIndexText(max, prop),
            ]
            : [
                soilColors.getValueIndex(min, prop),
                soilColors.getValueIndex(max, prop),
            ]

    }

}

function usesMorganAnalysis(layer: IDatalayerMapInstance) {

    switch (layer.typeId) {

        case LayerTypeId.P2O5:
        case LayerTypeId.K2O:
        case LayerTypeId.MgO:
            return !!layer.datalayer?.PkMorganAnalysis

        default:
            return false

    }
}

function getMorganIndexText(ppm: number, nutrient: string) {
    return getMorganIndexTextFromValue(nutrient as any, ppm)
}
